/**
 * Cached model / paper layer census for the paper-space viewport orchestrator
 * (`Scene3DStage.tsx` `ViewportRenderOrchestrator`), keyed on EVERY input
 * that can change it — Codex PR #784 round 3 P1 #2 ("recompute the satellite
 * layer census after the async lift").
 *
 * # The bug this closes
 *
 * `computeSpaceLayerCensus` walks the spatial index AND the parent →
 * render-only satellites map (`satelliteLayerProjection.ts`). The orchestrator
 * recomputed it only when the spatial-index instance or `geometryVersion`
 * changed. On the cache-direct path with more than
 * `CACHE_DIRECT_ASYNC_LIFT_THRESHOLD` stubs, `runSceneBulkLoad` CLEARS the
 * satellite map at the top of the pass and the lift runs asynchronously: the
 * first paper-space frame after the import's `geometryVersion` change cached
 * a census over the EMPTY map, `finishSceneBulkLoad` then repopulated the map
 * in place (same Map identity, same spatial index, same geometry version) and
 * nothing invalidated the cache — a child-only layer stayed absent from
 * `modelLayers`, so its bucket read as "paper-only" and was hidden in every
 * viewport until an unrelated mutation.
 *
 * # The fix
 *
 * The bulk-load publishes a satellite-map REVISION (`renderSatellitesVersionRef`,
 * bumped whenever the map is cleared or repopulated — sync AND async paths),
 * the same prev-vs-current comparison channel the orchestrator already uses
 * for `geometryVersion`. This cache folds all three keys (spatial identity,
 * geometry version, satellite revision) into one `sync` so the census — and
 * the scene-traversal caches that key on it — can never go stale while any
 * of its inputs moved.
 *
 * # The per-frame relift path — a monotonic UNION, not a revision bump
 *
 * `FrameLoops` Pass 2 also (re)populates the map — a culled parent's
 * satellites are dropped in Pass 1 and re-expanded on relift — and "every
 * INSERT is lifted at bulk-load" is NOT a contract this census may rely on:
 * the `Block` exemption in `lod/lodPolicy.ts` (`isAlwaysLiftedStubType`) was
 * removed in Codex PR #784 round-8 P2, so a sub-pixel reference reaches the
 * map for the FIRST time from Pass 2, with satellite layers this census has
 * never seen.
 * Bumping the revision from Pass 2 would recompute the census —
 * O(#spatial-index ids × satellites) — on EVERY frame that relifts a parent
 * (each pan / zoom step over a drawing with block references). Instead Pass 2
 * calls {@link SpaceLayerCensusCache.addSatellites}: O(#relifted children)
 * per relift, no walk of the index. Growing the census is safe because BOTH
 * of its consumers only ever HIDE on it — a bucket is pre-hidden on the sheet
 * when its layer is model-only, and hidden inside a viewport when its layer
 * is NOT a model layer — so a superset can only stop hiding a bucket whose
 * layer really does carry model content; a full recompute (next key change)
 * yields the same superset since the relifted satellites are then in the map.
 * `sync` reports a growth as a recompute (`true`) so the orchestrator's
 * scene-traversal caches, which key on the census, are rebuilt with it.
 */

import { addLayerChain } from "../layerChainVisibility";
import type { RenderableEntity, SpatialIndex } from "../types";
import {
  computeSpaceLayerCensus,
  type SatellitesByParent,
} from "./satelliteLayerProjection";

/** The inputs the census depends on. */
export interface SpaceLayerCensusInputs {
  spatial: SpatialIndex | null;
  /** `renderTopologyVersion` — bumped on entity mutation / import. */
  geometryVersion: number;
  /** `renderSatellitesVersionRef.current` — bumped when the satellite map is (re)populated. */
  satellitesVersion: number;
  /** The parent → satellites map itself (read in place; its identity is NOT a key). */
  satellitesByParent?: SatellitesByParent;
}

export interface SpaceLayerCensusCache {
  /** Layers with ≥1 model-space entity (or satellite of one). */
  readonly modelLayers: ReadonlySet<string>;
  /** Layers with ≥1 paper-space entity (or satellite of one). */
  readonly paperLayers: ReadonlySet<string>;
  /**
   * Recompute the census when any key changed since the last call (the first
   * call always computes). Returns `true` when it recomputed OR when
   * {@link addSatellites} grew the census since the previous `sync` — the
   * caller invalidates whatever it derived from the previous census.
   */
  sync(inputs: SpaceLayerCensusInputs): boolean;
  /**
   * Monotonic union (module doc, "per-frame relift path"): add each
   * satellite's OWN layer and its block-reference layer chain (`addLayerChain`
   * — the layers its bucket is keyed by) to the set of the PARENT's space
   * (`spaceId === "model"` → `modelLayers`, else `paperLayers` — a satellite
   * counts toward its parent's space, exactly as `computeSpaceLayerCensus`
   * classifies it; a paper-space INSERT's child added to `modelLayers` would
   * be pre-hidden on the sheet as "model-only"). Returns `true` when at least
   * one layer was absent. Safe to call before the first `sync` (that computes
   * from scratch over a map that already holds the satellites).
   */
  addSatellites(spaceId: string, satellites: ReadonlyArray<RenderableEntity>): boolean;
}

export function createSpaceLayerCensusCache(): SpaceLayerCensusCache {
  let primed = false;
  let keySpatial: SpatialIndex | null = null;
  let keyGeometryVersion = 0;
  let keySatellitesVersion = 0;
  let modelLayers = new Set<string>();
  let paperLayers = new Set<string>();
  /** Set by `addSatellites` when it grew a set; cleared by the next `sync`. */
  let grown = false;
  return {
    get modelLayers() {
      return modelLayers;
    },
    get paperLayers() {
      return paperLayers;
    },
    sync(inputs) {
      if (
        primed &&
        inputs.spatial === keySpatial &&
        inputs.geometryVersion === keyGeometryVersion &&
        inputs.satellitesVersion === keySatellitesVersion
      ) {
        // Keys unchanged: the census is current (a union since the last call
        // is already a superset of what a recompute would yield), but a growth
        // must still invalidate what the caller derived from the previous set.
        const changed = grown;
        grown = false;
        return changed;
      }
      primed = true;
      grown = false;
      keySpatial = inputs.spatial;
      keyGeometryVersion = inputs.geometryVersion;
      keySatellitesVersion = inputs.satellitesVersion;
      const census = computeSpaceLayerCensus(inputs.spatial, inputs.satellitesByParent);
      modelLayers = census.modelLayers;
      paperLayers = census.paperLayers;
      return true;
    },
    addSatellites(spaceId, satellites) {
      const target = spaceId === "model" ? modelLayers : paperLayers;
      const before = target.size;
      for (const child of satellites) addLayerChain(child, target);
      if (target.size === before) return false;
      grown = true;
      return true;
    },
  };
}
